import classnames from 'classnames';
// imports modules from common package
import {
	BillingStatus,
	FeatureFlags,
	Header as HeaderModule,
	Layout,
	Modal,
	Payment,
	Terms,
	Toast,
	Utils,
} from 'common';
import AccountVerificationModal from 'common/src/code/accountVerification/AccountVerificationModal.component';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import isFunction from 'lodash/isFunction';
import React, { Component } from 'react';
import { BrowserRouter } from 'react-router-dom';
import AccountVerificationWrapper from './accountVerification/AccountVerificationWrapper.component';
import BillingStatusWrapper from './billingStatus/BillingStatus.component';
import BrandedTrackingWrapper from './brandedTracking/BrandedTrackingWrapper.component';
import { CenterView } from './CenterView.component';
import AddCarrierWithCallback from './carrier/AddCarrierWithCallback.component';
import ManageCarrierWrapper from './carrier/ManageCarrierWrapper.component';
import ClientPendoView from './clientPendo/ClientPendoView.component';
import { LeftMenuViewWithRouter } from './LeftMenuView.component';
import PackingSlipsWrapper from './packingSlips/PackingSlipsWrapper.component';
import RefundEligibilityReport from './refundEligibilityReport/RefundEligibilityReport.component';
import SettingsWrapper from './settings/SettingsWrapper.component';
import UserActivation from './users/UserActivation.component';
import WinClientHomePage from './winClientHome/WinClientHomePage.component';

const { Analytics, Header, StoreHeader } = HeaderModule;
const { ModalContainer } = Modal;
const { Analytics: AnalyticsUtils, Session, Util } = Utils;
const { CenterPanel, CollapsibleSidePanel, TopPanel, Viewport, RegionType } =
	Layout;
const { TermsWrapper } = Terms;
const { AutoFundAccount } = Payment;
const { BillingStatusModal } = BillingStatus;
const { LaunchDarklyUtil } = FeatureFlags;
const { withToast } = Toast;

class MainView extends Component {
	constructor(props) {
		super(props);

		const showActivate = Util.isInRoute('activate');
		const showBillingStatus = Util.isInRoute('billingStatus');
		const showBrandedTracking = Util.isInRoute('brandedTracking');
		const showCarrier = Util.isInRoute('carrier');
		const showPackingSlips = Util.isInRoute('packingSlips');
		const showRefundEligibilityReport = Util.isInRoute(
			'refundeligibilityreport'
		);
		const showSettings = Util.isInRoute('settings');
		const showAccountVerify = Util.isInRoute('verify');
		const showTerms = Util.isInRoute('terms');
		const showWinClientHomePage = Util.isInRoute('winclienthome');
		const showAutoFundAccount = Util.isInRoute('autofundaccount');
		const showClientPendoView = Util.isInRoute('clientpendoview');
		const { accountStatus } = Session.getAccountInfo();
		let showEmailVerification = false;

		if (accountStatus === 'accountVerificationRequired') {
			showEmailVerification = true;
		}

		const showInRouteView =
			showActivate ||
			showAutoFundAccount ||
			showBrandedTracking ||
			showCarrier ||
			showPackingSlips ||
			showSettings ||
			showAccountVerify ||
			showTerms ||
			showWinClientHomePage ||
			showClientPendoView ||
			showBillingStatus;

		this.state = {
			// all in-route views
			showActivate,
			showAutoFundAccount,
			showBillingStatus,
			showBrandedTracking,
			showCarrier,
			showPackingSlips,
			showRefundEligibilityReport,
			showSettings,
			showAccountVerify,
			showTerms,
			showWinClientHomePage,
			showClientPendoView,
			// `true` if any of in-route is true
			showInRouteView,
			showStandAloneView: showRefundEligibilityReport,
			showBillingStatusModal: Session.showBillingStatusModal(),
			showEmailVerification,
		};

		if (isFunction(props.initializeChatbot)) {
			props.initializeChatbot(!showInRouteView);
		}
	}

	componentDidMount() {
		const { ldClient } = this.props;
		if (ldClient) {
			Session.setFeatureFlags(ldClient.allFlags());
			LaunchDarklyUtil.logIntializeError(ldClient);
			const accountInfo = Session.getAccountInfo();
			AnalyticsUtils.initLogRocket(ldClient, accountInfo);
		}

		const { showBillingStatusModal, showBillingStatus, showEmailVerification } =
			this.state;
		const {
			modalContext: { showModal },
		} = this.props;

		// show billing status modal as long as we're not just showing the standalone billing status view
		if (showBillingStatusModal && !showBillingStatus) {
			showModal({ component: BillingStatusModal });
		}

		if (showEmailVerification) {
			showModal({
				component: AccountVerificationModal,
				data: {
					completionCallback: this.accountVerificationCallback,
					emailOnly: true,
				},
			});
		}
	}

	/**
	 * Return true to hide the region; otherwise false.
	 * @param {RegionType} region
	 * @return {boolean}
	 */
	isRegionHidden = region => {
		const {
			// `true` if user is in win client
			IsInWinClient,

			// `true` if user is in Shopify store
			isInWebStore,

			// `true` if integrators use the account pages from GetURL
			isIntegrator,

			// `true` if the view is embedded in a Modal
			isInModal,
		} = Session.getSettings();

		if (region === RegionType.North) {
			// hide the header if user is in win client or shopify store, or integrators use the account pages
			return (
				IsInWinClient === true || isInWebStore === true || isIntegrator === true
			);
		} else if (region === RegionType.West) {
			return (
				// hide the west region (menu) if the view is embedded in a modal
				isInModal === true ||
				// hide the west region (menu) if user is in shopify store
				isInWebStore === true
			);
		}

		return true;
	};

	/**
	 * Show a specific in-route view when URL contains:
	 *  /activate - Activation
	 *  /carrier - Carrier
	 *  /packingSlips - Packing Slips
	 *  /verify - AccountVerify
	 *  /terms - Terms
	 *  /idv - Plaid Identity Verification
	 * @returns {ReactElement}
	 */
	renderInRouteView = () => {
		const {
			showActivate,
			showAutoFundAccount,
			showBillingStatus,
			showBrandedTracking,
			showCarrier,
			showClientPendoView,
			showPackingSlips,
			showSettings,
			showAccountVerify,
			showTerms,
			showWinClientHomePage,
		} = this.state;

		if (showActivate) {
			return <UserActivation />;
		}
		if (showAutoFundAccount) {
			return <AutoFundAccount />;
		} else if (showSettings) {
			return <SettingsWrapper />;
		} else if (showBrandedTracking) {
			return <BrandedTrackingWrapper />;
		} else if (showCarrier) {
			if (Util.isInRoute('add/ups')) {
				return <AddCarrierWithCallback carrierName="ups" />;
			} else if (Util.isInRoute('add/fedex')) {
				return <AddCarrierWithCallback carrierName="fedex" />;
			} else if (Util.isInRoute('add/dhlexpress')) {
				return <AddCarrierWithCallback carrierName="dhlexpress" />;
			} else {
				return <ManageCarrierWrapper hasHeader={!Util.isInRoute('noheader')} />;
			}
		} else if (showPackingSlips) {
			return <PackingSlipsWrapper />;
		} else if (showAccountVerify) {
			return <AccountVerificationWrapper />;
		} else if (showTerms) {
			return <TermsWrapper />;
		} else if (showWinClientHomePage) {
			return <WinClientHomePage />;
		} else if (showClientPendoView) {
			return <ClientPendoView />;
		} else if (showBillingStatus) {
			return <BillingStatusWrapper />;
		}
		return null;
	};

	/**
	 * Show stand-alone view when URL contains:
	 *  /refundedibilityreport - Refund Eligibility Report
	 *
	 * @returns {ReactElement}
	 */
	renderStandAloneView = () => {
		const { showRefundEligibilityReport } = this.state;

		if (showRefundEligibilityReport) {
			return <RefundEligibilityReport />;
		}
		return null;
	};

	/**
	 * Render the core main view.
	 * @returns {ReactElement}
	 */
	renderCoreMainView = () => {
		const { isIntegrator, isInAtgStore, IsInWinClient } = Session.getSettings();

		// WEBAPPS-28746, storeContext value "integrator" will be using the same styles as the WinClient
		const westCollapsiblePanelCls = classnames({
			'win-client': IsInWinClient === true || isIntegrator === true,
		});

		/*
		 * 	__BASENAME__: this global constant is defined in webpack.config.
		 *  This is the base URL for child routes which is different between
		 *  development and production builds.
		 */

		return (
			<BrowserRouter basename={__BASENAME__}>
				<TopPanel region={RegionType.North}>
					{/* render a simple store when user is in ATG store */}
					{isInAtgStore ? <StoreHeader /> : <Header />}
					<Analytics />
				</TopPanel>
				<CollapsibleSidePanel
					region={RegionType.West}
					title="Account Application"
					className={westCollapsiblePanelCls}
				>
					<LeftMenuViewWithRouter />
				</CollapsibleSidePanel>
				<CenterPanel>
					<CenterView {...this.props} />
				</CenterPanel>
			</BrowserRouter>
		);
	};

	render() {
		const { showInRouteView, showStandAloneView } = this.state;

		if (!showInRouteView && Session.isAccountVerificationRequired()) {
			return <AccountVerificationWrapper app="account" />;
		}

		// render any of in-route views
		if (showInRouteView) {
			return (
				<Viewport>
					<ModalContainer>{this.renderInRouteView()}</ModalContainer>
				</Viewport>
			);
		}

		// render stand-alone view (page with non-menu header)
		if (showStandAloneView) {
			return (
				<Viewport
					layout={{
						[RegionType.North]: {
							height: 50,
							isHidden: this.isRegionHidden(RegionType.North),
						},
						[RegionType.South]: {
							isHidden: this.isRegionHidden(RegionType.South),
						},
					}}
				>
					<TopPanel region={RegionType.North}>
						<Header plainHeader />
					</TopPanel>
					<ModalContainer className="center-view">
						{this.renderStandAloneView()}
					</ModalContainer>
				</Viewport>
			);
		}

		// render core main view of Account
		return (
			<Viewport
				layout={{
					[RegionType.North]: {
						height: 50,
						isHidden: this.isRegionHidden(RegionType.North),
					},
					[RegionType.West]: {
						width: 240,
						minWidth: 50,
						maxWidth: 480,
						resizable: true,
						isHidden: this.isRegionHidden(RegionType.West),
					},
				}}
			>
				<ModalContainer>{this.renderCoreMainView()}</ModalContainer>
			</Viewport>
		);
	}
}

export default withLDConsumer()(withToast(MainView));
